/**
 * @fileoverview Age utility functions for DOB-based chat restrictions
 */

import { AccountType } from '../services/auth/AnonymousAccountManager';

/**
 * Calculate age from date of birth
 * @param dob - ISO date string (YYYY-MM-DD)
 * @returns Age in years
 */
export function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Check if user is 16 or older
 * @param dob - ISO date string (YYYY-MM-DD) or undefined
 * @returns true if 16+, false otherwise (including if DOB is missing)
 */
export function isAge16OrOlder(dob: string | undefined): boolean {
  if (!dob) return false; // No DOB means restricted (safe default)
  return calculateAge(dob) >= 16;
}

/**
 * Validate DOB format and reasonable range
 * @param dob - ISO date string to validate
 * @returns { valid: boolean, error?: string }
 */
export function validateDOB(dob: string): { valid: boolean; error?: string } {
  if (!dob) {
    return { valid: false, error: 'Date of birth is required' };
  }

  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }

  const today = new Date();
  const age = calculateAge(dob);

  // Must be at least 5 years old (reasonable minimum)
  if (age < 5) {
    return { valid: false, error: 'Invalid date of birth' };
  }

  // Must be less than 120 years old (reasonable maximum)
  if (age > 120) {
    return { valid: false, error: 'Invalid date of birth' };
  }

  // Cannot be in the future
  if (birthDate > today) {
    return { valid: false, error: 'Date of birth cannot be in the future' };
  }

  return { valid: true };
}

export type ChatEligibility = 'full' | 'limited' | 'none';

/**
 * Determine chat eligibility based on account type and DOB
 * @returns 'full' | 'limited' | 'none'
 *   - 'full': Can chat, can toggle, sees opponent messages
 *   - 'limited': Cannot chat, but sees "sign in" prompt and opponent notifications (temp accounts)
 *   - 'none': No chat UI at all (under 16 or real accounts without DOB)
 */
export function getChatEligibility(
  accountType: AccountType,
  dob: string | undefined
): ChatEligibility {
  // Temp accounts get limited view (advertisement to sign up)
  if (accountType === 'temp' || accountType === 'new') {
    return 'limited';
  }

  // Real accounts: check age
  if (accountType === 'real') {
    // No DOB on file = no chat (existing users must add DOB)
    if (!dob) {
      return 'none';
    }

    if (!isAge16OrOlder(dob)) {
      return 'none'; // Under 16: no chat at all
    }

    return 'full'; // 16+: full chat access
  }

  return 'none'; // Default safe
}
